'use client';

import { type ComponentProps, type CSSProperties } from 'react';
import {
  AppSidebar,
  AppSidebarFooter,
  AppHeaderControls,
  type SidebarNavItem,
} from '@owlcat/app-shell';
import { Film, Plus, UserRound, Shield, ShieldCheck } from 'lucide-react';
import { Link, usePathname } from '@/lib/i18n/navigation';

/*
  Authenticated product chrome = the FLEET-SHARED <AppSidebar> from @owlcat/app-shell
  (same rail / mobile bar / drawer as stories and habits) bound to film's nav + footer.
    • desktop  → sticky sidebar rail on the left, content on the right
    • mobile   → fixed top bar + slide-in drawer (owned by the package)
  Nav: My films · New film · My characters; admins (inherited from accounts) also get
  the secondary group Character templates · AI models. The footer is the profile
  cluster + live usage % + support + legal. All URLs/labels resolve server-side in
  <ProductShell/>; this client component only binds them.
*/

export interface AppShellLabels {
  brand: string;
  films: string;
  newFilm: string;
  /** the author's saved reusable characters (user page) */
  myCharacters: string;
  /** admins (inherited from accounts): the single entry into the admin area (own sidebar) */
  admin: string;
  /** admins: the fleet admin in accounts (users, billing, subscriptions, comps) — same entry in every app */
  accountsAdmin: string;
  support: string;
  privacy: string;
  cookies: string;
  terms: string;
  /** mobile sidebar toggle */
  menu: string;
}

export interface AppShellProps {
  locale: string;
  isAdmin: boolean;
  controls: ComponentProps<typeof AppHeaderControls>;
  allowance: { pct: number | null; low: boolean; label: string; href: string };
  legal: { privacyUrl: string; termsUrl: string };
  labels: AppShellLabels;
  children: React.ReactNode;
}

const ICON = 'h-5 w-5 shrink-0';

export function AppShell({ isAdmin, controls, allowance, legal, labels, children }: AppShellProps) {
  const pathname = usePathname();

  const items: SidebarNavItem[] = [
    { id: 'films', href: '/', icon: <Film className={ICON} />, label: labels.films },
    { id: 'characters', href: '/characters', icon: <UserRound className={ICON} />, label: labels.myCharacters },
    // One entry into the admin area — it has its own sidebar (dashboard, templates, AI models…),
    // like stories / habits.
    ...(isAdmin ? [
      { id: 'admin', href: '/admin', icon: <Shield className={ICON} />, label: labels.admin, secondary: true },
      { id: 'accounts-admin', href: `${(controls.profile.accountsUrl ?? '').replace(/\/+$/, '')}/admin`, icon: <ShieldCheck className={ICON} />, label: labels.accountsAdmin, secondary: true },
    ] : []),
  ];

  // Footer = the FLEET footer (AppSidebarFooter, as habits/stories): [allowance % pill · profile /
  // language / theme] on one row, then support, then legal. The pill is the shared SubscriptionPill
  // (allowance face): seeded from the server, re-fetched from /api/allowance, opens the in-app
  // SubscribeWidget (billing deep-link only when the widget is not mounted).
  const footer = (close: () => void) => (
    <AppSidebarFooter
      pill={{ kind: 'allowance', appId: 'film', pct: allowance.pct, low: allowance.low, label: allowance.label, fallbackHref: allowance.href, refreshUrl: '/api/allowance' }}
      profile={<AppHeaderControls {...controls} />}
      support={{ label: labels.support }}
      legal={{ privacyUrl: legal.privacyUrl, termsUrl: legal.termsUrl, labels: { privacy: labels.privacy, cookies: labels.cookies, terms: labels.terms } }}
      onNavigate={close}
    />
  );

  // The package's mobile top bar sizes itself from --app-topbar-h; the content column
  // pads by the same amount so nothing hides under the fixed bar.
  const frameStyle = { '--app-topbar-h': '3.5rem' } as CSSProperties;

  return (
    <div className="flex min-h-dvh bg-background" style={frameStyle}>
      <AppSidebar
        brand={{ label: labels.brand, emoji: '🎬', href: '/' }}
        // The ONE create action = a filled CTA above the destinations (not a nav row).
        cta={{ id: 'new', href: '/new', icon: <Plus className={ICON} />, label: labels.newFilm }}
        items={items}
        activePath={pathname}
        linkComponent={Link}
        footer={footer}
        menuLabel={labels.menu}
      />
      <div className="flex min-w-0 flex-1 flex-col pt-[var(--app-topbar-h)] lg:pt-0">
        <main className="mx-auto w-full max-w-[1600px] flex-1 px-4 py-8 sm:px-8">{children}</main>
      </div>
    </div>
  );
}
